import { createAsyncThunk } from '@reduxjs/toolkit';
import { tr } from '../../i18n/i18n';
import i18n from '../../i18n/i18n';
import {
  ExamDetailsResponse,
  ExamByIdDetailsResponse,
  CreateExamRequest,
  CreateExamResponse,
  ExamPayload,
  UpdateExamResponse,
  ExamSubjectDetailsResponse,
  PublishExamResponse,
  CompleteExamResponse,
  CancelExamResponse,
  IExamAcknowledgementParams,
  IExamAcknowledgementResponse,
} from '../../interface/examDetails.interface';
import { IErrorResponse } from '../../interface/main.interface';
import {
  addExam,
  examCancel,
  examPublish,
  examRelease,
  getExamAcknowledgements,
  getExamById,
  getExams,
  getExamSubjectById,
  updateExam,
} from '../../services/examManagement/exam.service';

// Type for error handling in async thunks
interface ApiError {
  status?: number;
  response?: {
    status?: number;
    data?: {
      message?: string;
    };
  };
  message?: string;
}

export const fetchExams = createAsyncThunk<
  ExamDetailsResponse,
  ExamPayload | undefined,
  { rejectValue: IErrorResponse }
>('exam/fetch', async (params, { rejectWithValue }) => {
  try {
    const response = await getExams(params);
    return response;
  } catch (err: unknown) {
    const error = err as ApiError;
    return rejectWithValue({
      status: error?.status || error?.response?.status || 500,
      message: error?.message || error?.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error'),
    });
  }
});

export const fetchExamById = createAsyncThunk<
  ExamByIdDetailsResponse,
  string,
  { rejectValue: IErrorResponse }
>('exam/fetch-by-id', async (id, { rejectWithValue }) => {
  try {
    const response = await getExamById(id);
    return response;
  } catch (err: unknown) {
    const error = err as ApiError;
    return rejectWithValue({
      status: error?.status || error?.response?.status || 500,
      message: error?.message || error?.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error'),
    });
  }
});

export const createExam = createAsyncThunk<
  CreateExamResponse,
  CreateExamRequest,
  { rejectValue: IErrorResponse }
>('exam/create', async (payload, { rejectWithValue }) => {
  try {
    const response = await addExam(payload);
    return response;
  } catch (err: unknown) {
    const error = err as ApiError;
    return rejectWithValue({
      message: error?.message || error?.response?.data?.message || i18n.t('toast.failed_to_create_exam', 'Failed to create exam.'),
    });
  }
});

export const updateExamAction = createAsyncThunk<
  UpdateExamResponse,
  { examId: string; body: CreateExamRequest },
  { rejectValue: IErrorResponse }
>('exam/update', async ({ examId, body }, { rejectWithValue }) => {
  try {
    const response = await updateExam(examId, body);
    return response;
  } catch (err: unknown) {
    const error = err as ApiError;
    return rejectWithValue({
      message: error?.message || error?.response?.data?.message || tr('g.failed_to_update_exam', 'Failed to update exam.'),
    });
  }
});

export const publishExam = createAsyncThunk<
  PublishExamResponse,
  string,
  { rejectValue: IErrorResponse }
>('exam/publish', async (examId, { rejectWithValue }) => {
  try {
    const response = await examPublish(examId);
    return response;
  } catch (err: unknown) {
    const error = err as ApiError;
    return rejectWithValue({
      message: error?.message || error?.response?.data?.message || i18n.t('toast.failed_to_publish_exam', 'Failed to publish exam.'),
    });
  }
});

export const releaseExam = createAsyncThunk<
  CompleteExamResponse,
  string,
  { rejectValue: IErrorResponse }
>('exam/release', async (examId, { rejectWithValue }) => {
  try {
    const response = await examRelease(examId);
    return response;
  } catch (err: unknown) {
    const error = err as ApiError;
    return rejectWithValue({
      message: error?.message || error?.response?.data?.message || i18n.t('toast.failed_to_release_exam', 'Failed to release exam.'),
    });
  }
});

export const cancelExam = createAsyncThunk<
  CancelExamResponse,
  string,
  { rejectValue: IErrorResponse }
>('exam/cancel', async (examId, { rejectWithValue }) => {
  try {
    const response = await examCancel(examId);
    return response;
  } catch (err: unknown) {
    const error = err as ApiError;
    return rejectWithValue({
      message: error?.message || error?.response?.data?.message || i18n.t('toast.failed_to_cancel_exam', 'Failed to cancel exam.'),
    });
  }
});

export const fetchExamSubjectById = createAsyncThunk<
  ExamSubjectDetailsResponse,
  string,
  { rejectValue: IErrorResponse }
>('exam/fetch-subject-by-id', async (id, { rejectWithValue }) => {
  try {
    const response = await getExamSubjectById(id);
    return response;
  } catch (err: unknown) {
    const error = err as ApiError;
    return rejectWithValue({
      status: error?.status || error?.response?.status || 500,
      message: error?.message || error?.response?.data?.message || i18n.t('toast.failed_to_fetch_exam_subject', 'Failed to fetch exam subject.'),
    });
  }
});

export const fetchExamAcknowledgements = createAsyncThunk<
  IExamAcknowledgementResponse,
  { examId: string; params?: IExamAcknowledgementParams },
  { rejectValue: IErrorResponse }
>('exam/fetch-acknowledgements', async ({ examId, params }, { rejectWithValue }) => {
  try {
    const response = await getExamAcknowledgements(examId, params);
    return response;
  } catch (err: unknown) {
    const error = err as ApiError;
    return rejectWithValue({
      status: error?.status || error?.response?.status || 500,
      message:
        error?.message || error?.response?.data?.message || i18n.t('toast.failed_to_fetch_acknowledgements', 'Failed to fetch acknowledgements.'),
    });
  }
});

export const examActions = {
  fetchExams,
  fetchExamById,
  createExam,
  updateExamAction,
  fetchExamSubjectById,
  publishExam,
  releaseExam,
  cancelExam,
  fetchExamAcknowledgements,
};
